/* eslint-disable graphql/template-strings */
import { useEffect, useState } from 'react';

import type { ApolloError } from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import CourseMembership from 'bundles/accomplishments/components/s12n-certificates/CourseMembership.graphql';
import CourseVerifyCodeQuery from 'bundles/accomplishments/components/s12n-certificates/CourseVerifyCodeQuery.graphql';
import OnDemandSpecializationMemberships from 'bundles/accomplishments/components/s12n-certificates/OnDemandSpecializationMemberships.graphql';
import type {
  CourseMembershipsQueryQuery,
  CourseMembershipsQueryQueryVariables,
} from 'bundles/accomplishments/components/s12n-certificates/__generated__/CourseMembership';
import type {
  CourseVerifyCodeQueryQuery,
  CourseVerifyCodeQueryQueryVariables,
} from 'bundles/accomplishments/components/s12n-certificates/__generated__/CourseVerifyCodeQuery';
import type {
  OnDemandSpecializationMembershipsQuery,
  OnDemandSpecializationMembershipsQueryVariables,
} from 'bundles/accomplishments/components/s12n-certificates/__generated__/OnDemandSpecializationMemberships';
import type {
  S12nCertificate,
  S12nCourseCertificate,
} from 'bundles/accomplishments/components/s12n-certificates/types';
import { gradePercentageValue } from 'bundles/course-v2/utils/gradePercentageValue';

// NOTE: This will be replaced with proper graphql queries as they are migrated
export const SpecializationCertificateQuery = gql`
  query S12nCertificateQuery($code: String!) {
    OnDemandSpecializationMembershipsV1Resource(code: $code)
      @rest(
        type: "OnDemandSpecializationMembershipsV1Resource"
        path: "onDemandSpecializationMemberships.v1/?q=byCode&code={args.code}&fields=signatureTrackProfile&includes=signatureTrackProfile"
        method: "GET"
      ) {
      elements @type(name: "AccomplishmentsS12nMembership") {
        id
        userId
        s12nId
      }
      linked @type(name: "AccomplishmentsS12nLinkedData") {
        signatureTrackProfilesV1 @type(name: "AccomplishmentsSignatureTrackProfile") {
          firstName
          lastName
          middleName
        }
      }
    }
  }
`;

type OnDemandSpecializationMembershipsResponse = {
  userId: number;
  s12nId: string;
};

type SignatureTrackProfilesResponse = {
  firstName: string;
  middleName: string;
  lastName: string;
};

type CertificateDataResponse = {
  OnDemandSpecializationMembershipsV1Resource: {
    elements: OnDemandSpecializationMembershipsResponse[];
    linked: {
      signatureTrackProfilesV1: SignatureTrackProfilesResponse[];
    };
  };
};
type CertificateVariables = {
  code: string;
};

// Types for better type safety
type CourseDataElement = NonNullable<
  NonNullable<CourseMembershipsQueryQuery['NaptimeQueries']['MembershipsV1Resource']['byCode']>['elements']
>[0];

type CourseCodeElement = NonNullable<
  NonNullable<CourseVerifyCodeQueryQuery['NaptimeQueries']['CertificateV1Resource']['multiGet']>['elements']
>[0];

// Separate function to find course data for a specific course ID
const findCourseData = (
  coursesData: CourseMembershipsQueryQuery[],
  courseId: string
): CourseDataElement | undefined => {
  return coursesData.find(
    (course) => course.NaptimeQueries.MembershipsV1Resource.byCode?.elements?.[0]?.id.split('~')[1] === courseId
  )?.NaptimeQueries.MembershipsV1Resource.byCode?.elements?.[0];
};

// Separate function to find course code data for a specific course ID
const findCourseCodeData = (
  coursesCodesData: CourseVerifyCodeQueryQuery,
  courseId: string
): CourseCodeElement | undefined => {
  return coursesCodesData.NaptimeQueries.CertificateV1Resource.multiGet?.elements.find(
    (course) => course?.id.split('~')[2] === courseId
  );
};

// Type guard to validate if course data is complete and valid
const isValidCourseData = (
  courseData: CourseDataElement | undefined,
  courseCodeData: CourseCodeElement | undefined
): courseData is NonNullable<CourseDataElement> => {
  return !!(courseData && courseCodeData && courseData.course?.name);
};

// Helper function to assert courseCodeData is valid
const assertValidCourseCodeData = (courseCodeData: CourseCodeElement | undefined): NonNullable<CourseCodeElement> => {
  if (!courseCodeData) {
    throw new Error('Course code data is required');
  }
  return courseCodeData;
};

// Separate function to format a single course certificate
const formatSingleCourseCertificate = (
  courseData: NonNullable<CourseDataElement>,
  courseCodeData: NonNullable<CourseCodeElement>,
  courseId: string,
  userName: string
): S12nCourseCertificate => {
  // TypeScript assertions are safe here because isValidCourseData has validated the data structure
  const course = courseData.course!;

  return {
    courseId,
    courseName: course.name!,
    courseWorkload: course.workload ?? '',
    userName,
    partnerLogo: course.partnerLogo ?? '',
    partnerNames: course.partners?.elements.filter((p) => !!p).map((partner) => partner!.name) ?? [],
    instructorNames:
      course.instructors?.elements.filter((i) => !!i).map((instructor) => instructor!.fullName ?? '') ?? [],
    certificateCode: courseCodeData.verifyCode ?? '',
    certificateGrade: gradePercentageValue({ grade: courseData.grade?.score }),
    certificateGrantDate: courseCodeData.grantedAt ?? 0,
  };
};

// Refactored formatCourseCertificate with separated concerns
const formatCourseCertificate = (
  coursesData: CourseMembershipsQueryQuery[],
  coursesCodesData: CourseVerifyCodeQueryQuery,
  courseId: string,
  userName: string
): S12nCourseCertificate | null => {
  // Find the course data
  const courseData = findCourseData(coursesData, courseId);
  const courseCodeData = findCourseCodeData(coursesCodesData, courseId);

  // Graceful degradation: Return null for invalid/private courses instead of crashing
  if (!isValidCourseData(courseData, courseCodeData)) {
    return null;
  }

  // Format the course certificate
  return formatSingleCourseCertificate(courseData, assertValidCourseCodeData(courseCodeData), courseId, userName);
};

const formatResponse = (
  certificateCode: string,
  s12nDataResponse: OnDemandSpecializationMembershipsQuery,
  userData: CertificateDataResponse,
  coursesData: CourseMembershipsQueryQuery[],
  coursesCodesData: CourseVerifyCodeQueryQuery
): S12nCertificate => {
  const s12nData = s12nDataResponse.NaptimeQueries.OnDemandSpecializationMembershipsV1Resource.byCode?.elements[0];
  const userName = [
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    userData.OnDemandSpecializationMembershipsV1Resource.linked.signatureTrackProfilesV1[0].firstName,
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    userData.OnDemandSpecializationMembershipsV1Resource.linked.signatureTrackProfilesV1[0].middleName,
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    userData.OnDemandSpecializationMembershipsV1Resource.linked.signatureTrackProfilesV1[0].lastName,
  ]
    .join(' ')
    .replace(/\s+/, ' ');

  // Graceful degradation: Filter out invalid/failed courses and only include successfully formatted ones
  // This allows the page to display even when some courses are private/unavailable
  const courseCertificates =
    s12nData?.s12n?.courseIds
      .filter((id) => !!id)
      .map((courseId) => formatCourseCertificate(coursesData, coursesCodesData, courseId!, userName))
      .filter((cert) => cert !== null) ?? [];

  return {
    userId: s12nData?.userId ?? 0,
    userName,
    specializationId: s12nData?.s12nId ?? '',
    specializationSlug: s12nData?.s12n?.slug ?? '',
    specializationName: s12nData?.s12n?.name ?? '',
    specializationFAQs:
      s12nData?.s12n?.metadata.additionalFaqs.map((faq) => ({
        answer: faq?.answer ?? '',
        question: faq?.question ?? '',
      })) ?? [],
    partnerNames: s12nData?.s12n?.partners?.elements.filter((p) => !!p).map((partner) => partner.name) ?? [],
    primaryCourseWorkload: courseCertificates[0]?.courseWorkload ?? '',
    certificateCode,
    certificateCompletionDate: s12nData?.lastModified || 0,
    certificateDescription: s12nData?.s12n?.metadata.certificateDescription ?? '',
    courseCertificates,
  };
};

const useFetchCourseDetailsData = (courseCodes: string[]) => {
  const [queryCourse] = useLazyQuery<CourseMembershipsQueryQuery, CourseMembershipsQueryQueryVariables>(
    CourseMembership,
    {
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );
  const [data, setData] = useState<CourseMembershipsQueryQuery[]>([]);

  const courseCodesHash = courseCodes.join('-');

  const loading = courseCodesHash.length && !data.length;

  useEffect(() => {
    if (courseCodesHash.length > 0) {
      Promise.all(
        courseCodes.map(async (code) => {
          try {
            const result = await queryCourse({ variables: { code } });
            return {
              data: result.data || null,
              error: result.error,
            };
          } catch (error) {
            return {
              data: null,
              error: error as ApolloError,
            };
          }
        })
      ).then((results) => {
        // Graceful degradation: Only include successful results in the data array
        // Failed course fetches (private/deprecated courses) are silently excluded
        const successfulData = results.filter((result) => result.data !== null).map((result) => result.data!);

        setData(successfulData);
      });
    }
    // we need to disable this because it asks me to add courseCodes, which, if added, creates infinte loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCodesHash]);

  return {
    data,
    loading,
  };
};

type S12nCertificateData = {
  data: S12nCertificate | null;
  loading: boolean;
  error?: ApolloError;
};

const useS12nCertificateData = (certificateCode: string): S12nCertificateData => {
  const {
    data: s12nData,
    loading: s12nLoading,
    error: s12nError,
  } = useQuery<OnDemandSpecializationMembershipsQuery, OnDemandSpecializationMembershipsQueryVariables>(
    OnDemandSpecializationMemberships,
    {
      context: { clientName: 'gatewayGql' },
      variables: {
        code: certificateCode,
      },
      skip: !certificateCode,
    }
  );

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery<CertificateDataResponse, CertificateVariables>(SpecializationCertificateQuery, {
    variables: { code: certificateCode },
    skip: !certificateCode,
  });

  const userId =
    s12nData?.NaptimeQueries.OnDemandSpecializationMembershipsV1Resource.byCode?.elements?.[0]?.userId ?? 0;

  const courseIds =
    s12nData?.NaptimeQueries.OnDemandSpecializationMembershipsV1Resource.byCode?.elements?.[0]?.s12n?.courseIds ?? [];

  const {
    data: coursesCodesData,
    loading: coursesCodesLoading,
    error: coursesCodesError,
  } = useQuery<CourseVerifyCodeQueryQuery, CourseVerifyCodeQueryQueryVariables>(CourseVerifyCodeQuery, {
    context: { clientName: 'gatewayGql' },
    variables: {
      ids: courseIds.map((courseId) => `${userId}~Course!~${courseId}`),
    },
    skip: !userId || !courseIds.length,
  });

  const courses = coursesCodesData?.NaptimeQueries.CertificateV1Resource.multiGet?.elements ?? [];

  const { data: coursesData, loading: coursesLoading } = useFetchCourseDetailsData(
    courses.map((c) => c?.verifyCode ?? '')
  );

  const loading = s12nLoading || userLoading || coursesLoading || coursesCodesLoading;

  // Graceful degradation: Only consider critical errors that should prevent showing the page
  // Course fetching errors are handled gracefully by filtering out failed courses
  const criticalError = s12nError || userError || coursesCodesError;

  if (!criticalError && !loading && s12nData && userData && coursesCodesData) {
    const data = formatResponse(certificateCode, s12nData, userData, coursesData, coursesCodesData);
    return {
      data,
      loading: false,
      error: criticalError,
    };
  }

  return {
    data: null,
    loading,
    error: criticalError,
  };
};

export { useS12nCertificateData };
